import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, AlertCircle } from 'lucide-react';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, db } from '../firebase/config'; // הוסף את db כאן
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore'; // הוסף את האימפורטים האלה

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/user-not-found') {
        setError('משתמש לא נמצא. נא לבדוק את האימייל.');
      } else if (error.code === 'auth/wrong-password') {
        setError('סיסמה שגויה. נא לנסות שוב.');
      } else if (error.code === 'auth/invalid-email') {
        setError('כתובת אימייל לא תקינה.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('יותר מדי ניסיונות. נא לחכות ולנסות שוב מאוחר יותר.');
      } else {
        setError('שגיאה בהתחברות. נא לבדוק את הפרטים ולנסות שוב.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      // Create Google Auth Provider with Calendar scopes
      const provider = new GoogleAuthProvider();
      
      // Add Calendar scopes for automatic permission request
      provider.addScope('https://www.googleapis.com/auth/calendar');
      provider.addScope('https://www.googleapis.com/auth/calendar.events');
      
      // Optional: Force account selection (useful for multiple Google accounts)
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Sign in with popup
      const result = await signInWithPopup(auth, provider);
      
      // Get the access token for Google Calendar API
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      
      // Store the access token for later use with Calendar API
      if (accessToken) {
        localStorage.setItem('google_calendar_token', accessToken);
        console.log('✅ Google Calendar access token saved');
      }

      // ⚠️ חשוב! יצירת או עדכון המשתמש ב-Firestore
      const user = result.user;
      const userDocId = user.email.replace(/[.@]/g, '_'); // התאם לפורמט שלך
      const userRef = doc(db, 'users', userDocId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // משתמש חדש - צור אותו ב-Firestore
        const newUserData = {
          authUid: user.uid,
          email: user.email,
          name: user.displayName || '',
          phone: user.phoneNumber || '',
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          isActive: true,
          role: 'employee', // ברירת מחדל
          weeklyCapacity: 40,
          departmentId: '', // תוסיף לוגיקה לבחירת מחלקה אם צריך
          teamId: '', // תוסיף לוגיקה לבחירת צוות אם צריך
          teamIds: [],
          teamManagerId: '',
          photoURL: user.photoURL || '',
          lastLogin: serverTimestamp()
        };

        await setDoc(userRef, newUserData);
        console.log('✅ New Google user created in Firestore:', newUserData);
      } else {
        // משתמש קיים - עדכן את הפרטים
        await setDoc(userRef, {
          lastLogin: serverTimestamp(),
          name: user.displayName || userDoc.data().name,
          photoURL: user.photoURL || userDoc.data().photoURL || '',
          email: user.email // וודא שהאימייל מעודכן
        }, { merge: true });
        
        console.log('✅ Existing Google user updated in Firestore');
      }

      console.log('✅ Google Sign-in successful:', result.user);
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Google Sign-in error:', error);
      
      // Handle specific Google Sign-in errors
      if (error.code === 'auth/popup-closed-by-user') {
        setError('החלון נסגר. נא לנסות שוב.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('החלון נחסם על ידי הדפדפן. נא לאפשר חלונות קופצים ולנסות שוב.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('בעיית רשת. נא לבדוק את החיבור לאינטרנט.');
      } else {
        setError('שגיאה בהתחברות עם Google. נא לנסות שוב.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-6" dir="rtl">
      <div className="max-w-md w-full">
        <div className="bg-white p-6 md:p-8 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto h-12 w-12 bg-blue-600 rounded-xl flex items-center justify-center mb-4">
              <LogIn className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">מערכת ניהול משימות</h2>
            <p className="mt-2 text-gray-600">התחבר לחשבונך כדי להמשיך</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-400 ml-3" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Google Sign-in Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading}
            className="w-full mb-6 bg-white border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 flex items-center justify-center gap-3 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {googleLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <span>מתחבר עם Google...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>התחבר עם Google</span>
                <span className="text-xs text-gray-500 mr-2">+ יומן</span>
              </>
            )}
          </button>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">או התחבר עם אימייל</span>
            </div>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                כתובת אימייל
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="הכנס את האימייל שלך"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="הכנס את הסיסמה שלך"
              />
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                  מתחבר...
                </div>
              ) : (
                'התחבר'
              )}
            </button>
          </form>

          {/* Benefits Notice */}
          <div className="mt-6 p-3 bg-blue-50 rounded-lg">
            <div className="text-xs text-blue-700 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
                <span className="font-medium">התחברות עם Google</span>
              </div>
              <p>מאפשרת סנכרון אוטומטי עם Google Calendar</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;